import {clamp} from '@benchling/js-utils';
import $ from 'jquery';

import * as ast from 'notebook/tables/excess/AST';
import {AbsoluteCellReference} from 'notebook/tables/excess/AST';
import {SpreadsheetFunction} from 'notebook/tables/excess/FunctionConstants';
import {getFunction} from 'notebook/tables/excess/functions';
import tokenizer, {Token} from 'notebook/tables/excess/Tokenizer';
import * as util from 'notebook/tables/excess/Util';

import {MultiDocumentCellColors, TableIndex} from '../TableConstants';
import {isBaseBoolean, isBaseNumericValueType} from '../typed-values/DataTypes';
import {TableLocationNameArgs, RelativeCellAddress} from './Evaluator';

interface AssignedCellColors {
  [color: string]: string;
}

export interface TryInsertOptions {
  index: TableIndex;
  start: number;
  end: number;
  cid: string;
  shift: boolean;
  cellLock?: Partial<ast.CellLock>;
}

interface EditingTableLocationNameArgs {
  editingTable: string | null;
  editingDocumentName: string | null;
}

const CELL_HIGHLIGHTS = 5;
export const DEFAULT_DOCUMENT_NAME = '__UNNAMED_DOCUMENT__';

function _cellColor(i: number): string {
  return `cell-highlight-${i % CELL_HIGHLIGHTS}`;
}

function _tokenize(formula: string): {
  prefix: string;
  tokens: Array<Token>;
  leftover: string | undefined;
} {
  let leftover: string | undefined;
  let prefix: string;
  let tokens: Array<Token>;
  const formulaText = util.getFormulaText(formula);
  if (formulaText == null) {
    // Not a formula, just text.
    prefix = '';
    tokens = [];
    leftover = formula;
  } else {
    prefix = '=';
    try {
      // Fully valid formula!
      tokens = tokenizer.tokenize(formulaText);
      leftover = '';
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      if (e instanceof util.FormulaTokenizeError) {
        // Some valid tokens, but in the end, there was leftover
        // @ts-expect-error
        ({tokens, leftover} = e);
      } else {
        // Something went wrong, let's treat the whole thing as leftover.
        tokens = [];
        leftover = formulaText;
      }
    }
  }
  return {prefix, tokens, leftover};
}

interface TokenVisitor<T> {
  visitPrefix(prefix: string, $prefix: JQuery, pos: number): void;

  visitToken(token: Token, $token: JQuery, pos: number): void;

  visitLeftover(leftover: string, $lefover: JQuery, pos: number): void;

  getState(): T;
}

/**
 * When not null, indicates that "insert mode"" is activated, where the user can manipulate the cell
 * reference described by `target`. If `target` is null, the user can insert a cell reference at the
 * current selection.
 */
export type CellInsertLoc = {target: RelativeCellAddress | null} | null;

export enum CellInsertMode {
  ENABLE = 'ENABLE',
  DISABLE = 'DISABLE',
  DETECT = 'DETECT',
}

export const CELL_INSERT_PLACEHOLDER_CLASS = 'cell-insert-placeholder';

const CELL_INSERT_HELP =
  'Use the mouse or arrow keys to reference cell(s). Tap <kbd style="scale:0.9">Ctrl</kbd> to toggle the behavior.';
/**
 * A token visitor that identifies where an insertion / replacement of a cell reference should occur when
 * using arrow keys to modify the formula.
 *
 * The visitor will style the insert / replacement location as a visual affordance to the user. This will
 * be reflected in state where CellInsertLoc will be non-null (when activated).
 */
class CellInsertVisitor implements TokenVisitor<CellInsertLoc> {
  /**
   * Index within the formula at which the cell reference token that can be inserted / replaced starts.
   *
   * If the insertion / replacement criteria is not met, this should be null.
   */
  private cellRefStart: number | null;
  /**
   * Index within the formula at which the cell reference token that can be inserted / replaced ends.
   *
   * For an insertion, `cellRefEnd` will be equal to `cellRefStart`.
   * If the insertion / replacement criteria is not met, this should be null.
   */
  private cellRefEnd: number | null;
  /**
   * Cell identifier for the cell reference token that can be replaced.
   *
   * For an insertion (or if the criteria for replacement is not met), this will be `null`.
   */
  private targetReplaceCell: RelativeCellAddress | null = null;

  /**
   * @param tableName Name of table being modified
   * @param selection Current selection within formula text
   * @param formula Formula text being modified
   * @param mode If the value is:
   *
   * - CellInsertMode.DETECT - the insert mode only activates when a new cell reference can be inserted
   *   adjacent to the selection and *not* when the selection is adjacent to an existing cell reference.
   *   This is an attempt at only enabling arrow key based cell insertion in cases where we think that the
   *   user unambiguously would want to insert a cell reference. We explicitly do not want to trigger cell
   *   reference changes when the user is trying to use the left/right arrow keys to move the caret.
   *
   * - CellInsertMode.ENABLE - insert mode is activated for both locations where a new cell reference can
   *   be inserted as well as adjacent to existing cell references. This makes sense when the user has
   *   performed an action that unambiguously indicates that they are trying to insert a cell reference, which
   *   is generally when they have actually done just that... or have triggered a keyboard shortcut to enable
   *   it.
   *
   * - CellInsertMode.DISABLE - the insert mode is explicitly deactivated, e.g. can be used in response
   *   to a keyboard shortcut to toggle off this feature.
   */
  constructor(
    tableName: string,
    documentName: string | null,
    selection: BenchlingSelection | null,
    formula: string,
    mode: CellInsertMode
  ) {
    const cellInsert = selection
      ? defaultExport.tryInsert(
          {tableName, documentName},
          {editingTable: null, editingDocumentName: null},
          formula,
          {
            index: [0, 0], // Arbitrarily insert "A1" . Value doesn't matter: we just need cellInsert?.replaced.
            start: selection.start,
            end: selection.end,
            cid: selection.cid,
            shift: false,
          }
        )
      : null;

    this.cellRefStart = cellInsert?.replaced.start ?? null;
    this.cellRefEnd = cellInsert?.replaced.end ?? null;
    if (
      mode === CellInsertMode.DISABLE ||
      // DETECT only activates if we see an insertion, not a replacement.
      (mode === CellInsertMode.DETECT && this.cellRefEnd !== this.cellRefStart)
    ) {
      this.cellRefEnd = null;
      this.cellRefStart = null;
    }
  }

  visitPrefix(prefix: string, $prefix: JQuery, pos: number): void {
    if (this.cellRefStart === this.cellRefEnd && this.cellRefStart === prefix.length) {
      $prefix.append(this.makeCellInsertPlaceholder());
    }
  }

  visitToken(token: Token, $token: JQuery, pos: number): void {
    const start = pos;
    const end = start + token.text.length;

    if (this.cellRefEnd === this.cellRefStart) {
      if (this.cellRefStart === end) {
        $token.append(this.makeCellInsertPlaceholder());
      }
    } else if (this.cellRefEnd === end && this.cellRefStart === start) {
      this.makeCellInsertReplace($token);
    }

    if (this.cellRefEnd === end) {
      this.targetReplaceCell =
        token.node instanceof ast.Cell
          ? token.node.id
          : token.node instanceof ast.CellRange
            ? token.node.end
            : null;
    }
  }

  visitLeftover(leftover: string, $leftover: JQuery, pos: number): void {}

  getState(): CellInsertLoc {
    if (this.cellRefStart == null && this.cellRefEnd == null) {
      return null;
    }
    return {
      target: this.targetReplaceCell,
    };
  }

  private addCellInsertTooltip($node: JQuery): void {
    // The caller is expected to call $.tooltip to activate the tooltips,
    // which happens in FormulaEditable's EditableComponent.componentDidMount.
    $node.attr('data-toggle', 'tooltip');
    $node.attr('data-html', 'true');
    $node.attr('title', CELL_INSERT_HELP);
  }

  private makeCellInsertPlaceholder(): JQuery {
    const $node = $('<span></span>');
    $node.addClass(CELL_INSERT_PLACEHOLDER_CLASS);
    this.addCellInsertTooltip($node);
    return $node;
  }

  private makeCellInsertReplace($node: JQuery): void {
    $node.addClass('cell-insert-existing');
    this.addCellInsertTooltip($node);
  }
}

class SyntaxHighlightVisitor implements TokenVisitor<MultiDocumentCellColors> {
  private cellColors: MultiDocumentCellColors = {};
  private assigned: AssignedCellColors = {};
  private pos: number = 0;
  private tableName: string;
  private documentName: string | null;

  constructor(tableName: string, documentName: string | null) {
    this.tableName = tableName;
    this.documentName = documentName;
  }

  getState(): MultiDocumentCellColors {
    return this.cellColors;
  }

  visitPrefix(prefix: string, $prefix: JQuery, pos: number): void {}

  visitToken(token: Token, $token: JQuery, pos: number): void {
    if (!token.isASTNode()) {
      return;
    }
    if (token.node instanceof ast.FunctionOperator) {
      $token.css({fontWeight: 700});
    } else if (token.node instanceof ast.Cell || token.node instanceof ast.CellRange) {
      // Choose a color, any color, and format the node.
      if (!this.assigned[token.text]) {
        this.assigned[token.text] = _cellColor(this.pos++);
      }
      const color = this.assigned[token.text];
      $token.addClass(color);

      // Update the table of values to be used for cell shading.
      for (const {row, column, table, document} of Array.from(token.node.dependencies())) {
        let cellTable = table;
        let cellDocument = document;
        if (cellTable == null) {
          cellTable = this.tableName;
        } // default to the current table if unspecified.
        if (cellDocument == null) {
          cellDocument = this.documentName ?? DEFAULT_DOCUMENT_NAME;
        } // default to the current document if unspecified.
        if (this.cellColors[cellDocument] == null) {
          this.cellColors[cellDocument] = {};
        }
        if (this.cellColors[cellDocument][cellTable] == null) {
          this.cellColors[cellDocument][cellTable] = {};
        }
        if (this.cellColors[cellDocument][cellTable][row] == null) {
          this.cellColors[cellDocument][cellTable][row] = {};
        }
        this.cellColors[cellDocument][cellTable][row][column] = color;
      }
    } else if (token.node instanceof ast.Value && token.node.typedValue.type === util.ValueType.STRING) {
      $token.addClass('cell-highlight-string');
    } else if (
      token.node instanceof ast.Value &&
      (isBaseNumericValueType(token.node.typedValue) || isBaseBoolean(token.node.typedValue))
    ) {
      $token.addClass('cell-highlight-value');
    }
  }

  visitLeftover(leftover: string, $leftover: JQuery, pos: number): void {
    if (leftover.startsWith('"')) {
      $leftover.addClass('cell-highlight-string');
    }
  }
}

function traverseFormula(formula: string, visitors: Array<TokenVisitor<unknown>>): JQuery {
  const $formula = $('<div></div>');
  const span = (text: string): JQuery => $('<span></span>').text(text);
  const {prefix, tokens, leftover} = _tokenize(formula);
  let pos = 0;
  if (prefix.length) {
    const $prefix = span(prefix);
    for (const visitor of visitors) {
      visitor.visitPrefix(prefix, $prefix, pos);
    }
    $formula.append($prefix);
    pos += prefix.length;
  }
  tokens.forEach((token) => {
    const $token = span(token.text);
    for (const visitor of visitors) {
      visitor.visitToken(token, $token, pos);
    }
    $formula.append($token);
    pos += token.text.length;
  });
  if (leftover?.length) {
    const $leftover = span(leftover);
    for (const visitor of visitors) {
      visitor.visitLeftover(leftover, $leftover, pos);
    }
    $formula.append($leftover);
    pos += leftover.length;
  }
  return $formula;
}

const defaultExport = {
  getFormattedFormula: (
    tableName: string,
    documentName: string | null,
    formula: string,
    selection?: {start: number; end: number; cid: string} | null,
    cellInsertMode: CellInsertMode = CellInsertMode.DETECT
  ): {
    formulaHTML: string;
    cellColors: MultiDocumentCellColors;
    cellInsertLoc: CellInsertLoc;
  } => {
    const visitors = {
      cellInsert: new CellInsertVisitor(tableName, documentName, selection ?? null, formula, cellInsertMode),
      syntaxHighlight: new SyntaxHighlightVisitor(tableName, documentName),
    };
    const $formula = traverseFormula(formula, Object.values(visitors));
    return {
      formulaHTML: $formula.html(),
      cellColors: visitors.syntaxHighlight.getState(),
      cellInsertLoc: visitors.cellInsert.getState(),
    };
  },

  tryInsert: (
    {tableName, documentName}: TableLocationNameArgs,
    {editingTable, editingDocumentName}: EditingTableLocationNameArgs,
    formula: string,
    {index, start, end, cid, shift, cellLock}: TryInsertOptions
  ): {
    formula: string;
    selection: {
      cid: string;
      start: number;
      end: number;
    };
    replaced: {start: number; end: number};
  } | null => {
    const {prefix, tokens, leftover} = _tokenize(formula);
    const sameDocument: boolean = documentName === editingDocumentName;
    const sameTable: boolean = sameDocument && tableName === editingTable;
    const cellId: RelativeCellAddress = {
      table: sameTable ? null : tableName,
      document: sameDocument ? null : documentName,
      row: index[0],
      column: index[1],
    };

    function makeFormula(
      start: number,
      end: number,
      cells: string,
      prespace: string,
      space: string
    ): {
      formula: string;
      selection: {
        cid: string;
        start: number;
        end: number;
      };
      replaced: {start: number; end: number};
    } {
      const before = formula.slice(0, start) + prespace;
      const after = space + formula.slice(end);
      const cursor = start + prespace.length + cells.length;
      return {
        formula: before + cells + after,
        selection: {cid, start: cursor, end: cursor},
        replaced: {start: before.length, end: formula.length - after.length},
      };
    }

    if (prefix !== '=' || start < prefix.length) {
      return null;
    } else {
      let cells = util.toCellReferenceTokenText(cellId, cellLock);

      // If there is just an equals sign, handle it.
      if (tokens.length === 0) {
        if (leftover?.length === 0) {
          return makeFormula(prefix.length, prefix.length, cells, '', '');
        }
        return null;
      } else {
        let tokenEnd;
        let tokenStart = prefix.length;

        // oxlint-disable-next-line no-inner-declarations
        function validPredecessor(i: number): boolean {
          if (tokens[i] == null) {
            return true;
          }
          if (tokens[i].isSpace()) {
            return validPredecessor(i - 1);
          }
          return (
            tokens[i].isLeftParen() ||
            tokens[i].isComma() ||
            (tokens[i].isASTNode() && tokens[i].node instanceof ast.BinaryOperator)
          );
        }

        // oxlint-disable-next-line no-inner-declarations
        function validSuccessor(i: number): boolean {
          if (tokens[i] == null) {
            return leftover?.length === 0;
          }
          if (tokens[i].isSpace()) {
            return validSuccessor(i + 1);
          }
          if (!tokens[i].isASTNode()) {
            return true;
          }
          return !(
            tokens[i].node instanceof ast.FunctionOperator ||
            tokens[i].node instanceof ast.Value ||
            tokens[i].isCellRef()
          );
        }

        for (let i = 0; i < tokens.length; i++) {
          // Process tokens as long as the cursor is after it.
          const token = tokens[i];
          tokenEnd = tokenStart + token.text.length;
          // If we are touching a cell, just directly replace it.
          if (tokenStart <= start && start <= end && end <= tokenEnd && token.isCellRef()) {
            // If we were using shift and the clicked cell is on the same table
            // as this cell, use that as a range. otherwise, just the new cell.
            if (shift) {
              let anchorId =
                token.node instanceof ast.Cell
                  ? token.node.id
                  : token.node instanceof ast.RelCellRange
                    ? token.node.start
                    : (token.node as ast.CellRange).start;

              const table = (token.node as AbsoluteCellReference).getTable() || editingTable;
              const document = (token.node as AbsoluteCellReference).getDoc() || editingDocumentName;

              if (table === tableName && document === documentName) {
                // Like cellId constructed above, drop the table name if we are referencing the editingTable;
                // otherwise, toCellRangeReferenceTokenText will throw.
                anchorId =
                  anchorId.table === editingTable && anchorId.document === editingDocumentName
                    ? {...anchorId, table: null, document: null}
                    : anchorId;

                cells =
                  anchorId.row === cellId.row && anchorId.column === cellId.column
                    ? util.toCellReferenceTokenText(cellId, cellLock)
                    : util.toCellRangeReferenceTokenText(anchorId, cellId);
              }
            }
            return makeFormula(tokenStart, tokenEnd, cells, '', '');
          }

          if (tokenStart === start) {
            // If the cursor is before us, see if the cellref can be inserted there.
            if (validPredecessor(i - 1) && validSuccessor(i)) {
              return makeFormula(tokenStart, tokenStart, cells, '', '');
            }
            return null;
          } else if (end < tokenEnd) {
            // Or we are in a space between two valid characters.
            if (validPredecessor(i - 1) && token.isSpace() && validSuccessor(i + 1)) {
              const prespace = token.text.slice(0, start - tokenStart);
              const postspace = token.text.slice(end - tokenStart);
              return makeFormula(tokenStart, tokenEnd, cells, prespace, postspace);
            }
            return null;
          } else {
            tokenStart = tokenEnd;
          }
        }

        // Check to see if the last token is a valid predecessor
        if (tokenEnd === start && start === end) {
          if (tokens && validPredecessor(tokens.length - 1)) {
            return makeFormula(tokenEnd, tokenEnd, cells, '', '');
          }
        }

        return null;
      }
    }
  },

  /**
   * Updates a formula's cell references after tables have been renamed in a paste action
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  updateCellReferencesForPaste: (cellText: string, oldToNewTableName: {[oldTableName: string]: string}) => {
    const {prefix, tokens} = _tokenize(cellText);
    // Short circuit if this is not an equation
    if (prefix === '=') {
      const updatedTokens: Array<string> = tokens.map((token) => {
        if (token.node instanceof ast.Cell) {
          const cellId = token.node.id;
          if (cellId.table && oldToNewTableName[cellId.table]) {
            // Since the cell reference contains a table that is in the paste contents,
            // update it to use the new table name
            cellId.table = oldToNewTableName[cellId.table];
          }
          return util.toCellReferenceTokenText(cellId, token.node.locks);
        }
        if (token.node instanceof ast.CellRange) {
          // Same as above if-statement, but updates the start and end of a cell range
          const cellRange = token.node;
          const startCellId = cellRange.start;
          const endCellId = cellRange.end;
          if (startCellId.table && oldToNewTableName[startCellId.table]) {
            startCellId.table = oldToNewTableName[startCellId.table];
          }
          if (endCellId.table && oldToNewTableName[endCellId.table]) {
            endCellId.table = oldToNewTableName[endCellId.table];
          }
          return util.toCellRangeReferenceTokenText(startCellId, endCellId, token.node.locks);
        }
        return token.text;
      });
      return prefix + updatedTokens.join('');
    }
    return cellText;
  },
  /**
   * Determines if a (shift) insertion can be done into the current table's formula with a selection of
   * {start, end, cid}.
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  canInsert: (
    {editingTable, editingDocumentName}: EditingTableLocationNameArgs,
    formula: string,
    {start, end, cid, shift}: {start: number; end: number; cid: string; shift: boolean}
  ) =>
    // We simulate inserting SomeTable!A1 into the given formula with selection [start, end], as which table
    // / cell being inserted has no effect on success. If it is a failure, tryInsert will return null.
    defaultExport.tryInsert(
      {tableName: 'SomeTable', documentName: null},
      {editingTable, editingDocumentName},
      formula,
      {
        index: [0, 0],
        start,
        end,
        cid,
        shift,
      }
    ) != null,

  completeFunctionName(formula: string, functionName: string): string {
    const result = _tokenize(formula);
    if (result.prefix === '=') {
      const partialFormula = `${result.tokens.map((token) => token.text).join('')}`;
      return `=${partialFormula}${functionName}(`;
    } else {
      return `=${functionName}(`;
    }
  },

  /**
   * Given a arrow-key direction, determine the relative cell.
   *
   * NOTE: This helper should be seldom used since it does not have the full context that TableEditable has.
   * A future refactor should hoist out the logic out of TableEditable, deprecating this method.
   */
  moveCell({
    index,
    lastIndex,
    dir,
  }: {
    index: TableIndex;
    lastIndex: TableIndex;
    dir: 'up' | 'down' | 'left' | 'right';
  }): TableIndex {
    let [i, j] = index;
    const [maxI, maxJ] = lastIndex;
    switch (dir) {
      case 'up':
        i -= 1;
        break;
      case 'down':
        i += 1;
        break;
      case 'left':
        j -= 1;
        break;
      case 'right':
        j += 1;
        break;
      default:
        break;
    }
    i = clamp(0, maxI, i);
    j = clamp(0, maxJ, j);
    return [i, j];
  },

  /**
   * Parses the formula to find the "open" function and current parameter index.
   *
   * @param formula Full, unsliced formula text
   * @param caretPosition Character index in formula representing the user's text cursor position
   * @returns function - Spreadsheet function in the outer scope.
   * @returns currentParameterIndex - Index of the current parameter of the function, equals -1 if the user's
   * text cursor is within a function name and it may be greater than the number of allowed arguments for the
   * function.
   * @returns null - if it could not find a valid function.
   */
  getScopedFunction(
    formula: string,
    caretPosition: number
  ): {function: SpreadsheetFunction; currentParameterIndex: number} | null {
    const result = _tokenize(formula);
    if (result.prefix !== '=' || result.tokens.length < 2 || caretPosition <= result.prefix.length) {
      return null;
    }

    let currentParameterIndex = 0;

    /**
     * Slice result.tokens list to not include tokens that come after the caretPosition, with special logic
     * to include a left paren token if the user's caret is "inside" a function name, even though the left
     * paren token is past caretPosition.
     * For example, a simple string slice with caretPosition=7 would give us "=SUM(12" or "=SUM(LE", while
     * this code would give us "=SUM(123" and "=SUM(LEFT(" instead, which lets us return the function name
     * that the user's cursor is inside.
     */
    const tokens: Array<Token> = []; // Tokens that come before the caret position
    let formulaLength = result.prefix.length; // Total length of Tokens in tokens, plus prefix length

    for (const token of result.tokens) {
      if (formulaLength >= caretPosition) {
        if (tokens[tokens.length - 1].isASTNode() && token.isLeftParen()) {
          // Add left paren token in order to recognize the last node as a function name.
          tokens.push(token);
          formulaLength += token.text.length;
          // The caret is inside a function name instead of inside a function call, so there is no "current parameter".
          currentParameterIndex = -1;
        }
        break;
      }

      tokens.push(token);
      formulaLength += token.text.length;
    }
    // Keep track of how many parentheses deep we are, since "=SUM(ABS()," should return SUM instead of ABS
    // The "open" function is the "first" (going backwards through tokens) ASTNode where nestingLevel < 0
    let nestingLevel = 0;

    for (let i = tokens.length - 1; i >= 0; i--) {
      const token = tokens[i];
      if (token.isRightParen()) {
        nestingLevel++;
      } else if (token.isLeftParen()) {
        nestingLevel--;
      } else if (nestingLevel === 0 && token.isComma()) {
        // Since nestingLevel === 0, we are in the open function
        currentParameterIndex++;
      } else if (nestingLevel < 0 && token.isASTNode() && token.node instanceof ast.FunctionOperator) {
        // In proper scope and is AST node
        const func = getFunction(token.node.type);
        return func ? {function: func, currentParameterIndex} : null;
      }
    }
    return null;
  },
  hasCrossStepReference(cellText: string): boolean {
    const formulaText = util.getFormulaText(cellText);
    if (formulaText == null) {
      return false;
    }
    const {tokens} = tokenizer.partialTokenize(formulaText, {relative: false});

    return (
      tokens != null &&
      tokens.some(
        (token) =>
          token.isASTNode() && token.node instanceof ast.AbsoluteCellReference && token.node.getDoc() != null
      )
    );
  },
};

export default defaultExport;
